import { useEffect, useRef } from "react";
import "./BodhiTextarea.css";

// Must match the CSS line-height + vertical padding so autoSize maths stays
// in sync with the rendered box.
const LINE_HEIGHT = 22;
const PAD_Y = 20;

/**
 * BodhiTextarea — branded multi-line input. Pure CSS, no antd.
 *
 * Props:
 *   value / onChange   controlled value + native change handler
 *   placeholder
 *   rows               initial row count (default 3)
 *   autoSize           { minRows, maxRows } — grows with content (antd-style)
 *   disabled
 *   className / style
 *   …rest              forwarded to the native <textarea>
 */
const BodhiTextarea = ({
	value,
	onChange,
	placeholder,
	rows = 3,
	autoSize,
	disabled = false,
	className = "",
	style,
	...rest
}) => {
	const ref = useRef(null);
	const minRows = autoSize?.minRows ?? rows;
	const maxRows = autoSize?.maxRows;

	// Grow/shrink the textarea to fit its content when autoSize is on.
	useEffect(() => {
		if (!autoSize) return;
		const el = ref.current;
		if (!el) return;
		el.style.height = "auto";
		let next = el.scrollHeight;
		if (maxRows) {
			const max = maxRows * LINE_HEIGHT + PAD_Y;
			next = Math.min(next, max);
			el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
		} else {
			el.style.overflowY = "hidden";
		}
		el.style.height = `${next}px`;
	}, [value, autoSize, maxRows]);

	const cls = [
		"bodhi-textarea",
		disabled && "bodhi-textarea-disabled",
		className,
	]
		.filter(Boolean)
		.join(" ");

	return (
		<textarea
			ref={ref}
			className={cls}
			value={value}
			onChange={onChange}
			placeholder={placeholder}
			disabled={disabled}
			rows={minRows}
			style={autoSize ? { resize: "none", ...style } : style}
			{...rest}
		/>
	);
};

export default BodhiTextarea;
